import {
  CustomPartsContainer,
  FormContainer,
  InventoryContainer,
} from "../../../../constants/Styles";
import { Formik } from "formik";
import FormikFormLine from "../../../formik-form-components/FormikFormLine";
import FormikCheckbox from "../../../formik-form-components/FormikCheckbox";
import FormikNumberInput from "../../../formik-form-components/FormikNumberInput";
import Button from "../../../common/Button";
import { ButtonTypes } from "../../../common/Button/constants";
import React, { useCallback, useState } from "react";
import * as Yup from "yup";
import settings from "../../../../constants/settings";
import useFetchAndTrace from "../../../../utils/hooks/fetchWithTrace";
import FormValidationError from "../../../common/FormValidationError";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import LoadingIcon from "../../../common/LoadingIcon";
import { Part } from "../../../../reducers/models/parts";
import LoadingOverlay from "../../../LoadingOverlay";
import Inventory from "../../../component-search/PartDrawer/Inventory";
import SelectUser from "../../../SelectUser";

import { UserSummary } from "../../../../reducers/models/users";

interface ConsignmentPartFormValues {
  name: string;
  shortDescription: string;
  requiresStencil: boolean;
  numThroughHoleJoints: number;
  userId: string;
}

const consignmentPartSchema = Yup.object().shape({
  name: Yup.string().required("This is a required field."),
  shortDescription: Yup.string().required("This is a required field."),
  requiresStencil: Yup.boolean(),
  numThroughHoleJoints: Yup.number().required().min(0),
  userId: Yup.string(),
});

const AddOrEditCustomPartForm = ({
  userId,
  selectedPart,
  onSavePart,
  isAdminOrEngineer,
}: {
  userId: string;
  selectedPart?: Part;
  onSavePart?: (part: Part) => void;
  isAdminOrEngineer: boolean;
}) => {
  const flashPCBSettings = useFlashPCBSettings();
  const fetchAndTrace = useFetchAndTrace();
  const [saving, setSaving] = useState<boolean>(false);
  const [errored, setErrored] = useState<boolean>(false);

  const errorHandler = useCallback(() => {
    setErrored(true);
  }, []);

  const savePartHandler = async (response: Response) => {
    setSaving(false);
    if (response.ok) {
      const part = (await response.json()) as Part;
      onSavePart?.(part);
    } else {
      throw Error("Failed to save part");
    }
  };

  const handleSaveCustomPart = (values: ConsignmentPartFormValues) => {
    setSaving(true);
    const url = selectedPart
      ? `${settings.api_url}/users/${values.userId}/parts/${selectedPart.id}`
      : `${settings.api_url}/users/${values.userId}/parts`;
    void fetchAndTrace(url, savePartHandler, errorHandler, {
      body: JSON.stringify({
        name: values.name,
        short_description: values.shortDescription,
        requires_stencil: values.requiresStencil,
        num_through_hole_joints: values.numThroughHoleJoints,
      }),
      method: selectedPart ? "PUT" : "POST",
    });
  };

  if (flashPCBSettings === null) {
    return <LoadingIcon />;
  }

  return (
    <CustomPartsContainer>
      <LoadingOverlay loading={saving} text={"Saving part"} />
      <Formik
        initialValues={
          selectedPart
            ? {
                name: selectedPart.name,
                shortDescription: selectedPart.short_description,
                requiresStencil: selectedPart.requires_stencil,
                numThroughHoleJoints: selectedPart.num_through_hole_joints,
                userId: selectedPart.user_id as string,
              }
            : {
                name: "",
                shortDescription: "",
                requiresStencil: false,
                numThroughHoleJoints: 0,
                userId: userId,
              }
        }
        onSubmit={handleSaveCustomPart}
        validationSchema={consignmentPartSchema}
      >
        {({ handleSubmit, isValid, values, setFieldValue }) => {
          const setSelectedUser = (user: UserSummary | null) => {
            if (user !== null) {
              void setFieldValue("userId", user.id);
            }
          };
          return (
            <FormContainer onSubmit={handleSubmit}>
              <FormikFormLine
                name={"name"}
                label={"Name: "}
                isRequired={true}
              />
              <FormikFormLine
                name={"shortDescription"}
                label={"Notes about this part: "}
                isTextArea={true}
                isRequired={true}
              />
              <FormikCheckbox
                name={"requiresStencil"}
                label={
                  "This part requires a stencil (it is a surface mount part)"
                }
              />
              <FormikNumberInput
                name={"numThroughHoleJoints"}
                label={"How many through hole joints does this part require?"}
                disabled={false}
              />
              {isAdminOrEngineer && (
                <SelectUser
                  selectedUserId={values.userId}
                  setSelectedUser={setSelectedUser}
                  isClearable={false}
                />
              )}
              <Button
                label={"Save part"}
                type={ButtonTypes.SUBMIT}
                disabled={!isValid}
              />
            </FormContainer>
          );
        }}
      </Formik>
      {errored && (
        <FormValidationError validationMessage={"Failed to save part"} />
      )}
      {isAdminOrEngineer && selectedPart && (
        <InventoryContainer>
          <Inventory part={selectedPart} />
        </InventoryContainer>
      )}
    </CustomPartsContainer>
  );
};

export default AddOrEditCustomPartForm;
